import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  EmojiEvents,
  ShowChart,
} from '@mui/icons-material';

interface BenchmarkData {
  accountId: string;
  companyName: string;
  benchmarks: {
    healthScore: {
      account: number;
      average: number;
      top25: number;
      percentile: number;
    };
    entities: Record<string, {
      accountCount: number;
      avgCount: number;
      top25Count: number;
      percentile: number;
    }>;
    timeToFirstEntity: {
      accountTTF: number;
      avgTTF: number;
      top25TTF: number;
      percentile: number;
    } | null;
    featureAdoption: {
      account: number;
      average: number;
      top25: number;
      percentile: number;
    };
    activity: {
      accountDays: number;
      avgDays: number;
      top25Days: number;
      percentile: number;
    } | null;
    cohort: {
      cohortSize: number;
      cohortAgeRange: string;
      avgHealthScore: number;
      top25HealthScore: number;
      accountHealthScore: number;
    } | null;
    stage: {
      stage: string;
      accountsInStage: number;
      avgHealthScore: number;
      top25HealthScore: number;
      accountHealthScore: number;
    } | null;
  };
  comparisonBase: {
    totalAccounts: number;
    cohortAccounts: number;
    sameStageAccounts: number;
  };
}

interface ComparisonBenchmarksProps {
  data: BenchmarkData;
}

export const ComparisonBenchmarks: React.FC<ComparisonBenchmarksProps> = ({ data }) => {
  const { benchmarks, comparisonBase } = data;

  const getTrendIcon = (accountValue: number, avgValue: number) => {
    const diff = accountValue - avgValue;
    if (diff > avgValue * 0.1) return <TrendingUp color="success" fontSize="small" />;
    if (diff < -avgValue * 0.1) return <TrendingDown color="error" fontSize="small" />;
    return <TrendingFlat color="action" fontSize="small" />;
  };

  const getPercentileColor = (percentile: number): 'success' | 'warning' | 'error' | 'default' => {
    if (percentile >= 75) return 'success';
    if (percentile >= 50) return 'warning';
    if (percentile >= 25) return 'error';
    return 'default';
  };

  const getPercentileLabel = (percentile: number): string => {
    if (percentile >= 90) return 'Top 10%';
    if (percentile >= 75) return 'Top 25%';
    if (percentile >= 50) return 'Above Average';
    if (percentile >= 25) return 'Below Average';
    return 'Bottom 25%';
  };

  const formatEntityName = (entityType: string): string => {
    return entityType
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Box>
      {/* Health Score Benchmark */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <ShowChart color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Health Score Performance
            </Typography>
          </Box>

          <Box display="flex" gap={3} flexWrap="wrap">
            <Box flex="1" minWidth="200px">
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Your Score
              </Typography>
              <Typography variant="h4" fontWeight={700} color="primary">
                {benchmarks.healthScore.account}
              </Typography>
              <Chip
                label={getPercentileLabel(benchmarks.healthScore.percentile)}
                color={getPercentileColor(benchmarks.healthScore.percentile)}
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>

            <Box flex="1" minWidth="200px">
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Average
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h5" fontWeight={600}>
                  {benchmarks.healthScore.average}
                </Typography>
                {getTrendIcon(benchmarks.healthScore.account, benchmarks.healthScore.average)}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Across {comparisonBase.totalAccounts} accounts
              </Typography>
            </Box>

            <Box flex="1" minWidth="200px">
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Top 25% Benchmark
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <EmojiEvents fontSize="small" sx={{ color: 'warning.main' }} />
                <Typography variant="h5" fontWeight={600}>
                  {benchmarks.healthScore.top25}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Top performers threshold
              </Typography>
            </Box>
          </Box>

          <Box mt={2}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Percentile: {benchmarks.healthScore.percentile}th (Better than {benchmarks.healthScore.percentile}% of accounts)
            </Typography>
            <LinearProgress
              variant="determinate"
              value={benchmarks.healthScore.percentile}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  bgcolor: benchmarks.healthScore.percentile >= 75 ? 'success.main' :
                           benchmarks.healthScore.percentile >= 50 ? 'warning.main' : 'error.main',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Feature Adoption Benchmark */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Feature Adoption
          </Typography>

          <Box display="flex" gap={2}>
            <Box flex="1">
              <Typography variant="body2" color="text.secondary">
                Features Adopted
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {benchmarks.featureAdoption.account}
              </Typography>
            </Box>
            <Box flex="1">
              <Typography variant="body2" color="text.secondary">
                Average
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {benchmarks.featureAdoption.average}
              </Typography>
            </Box>
          </Box>

          <Box mt={2}>
            <Chip
              label={`${benchmarks.featureAdoption.percentile}th percentile`}
              color={getPercentileColor(benchmarks.featureAdoption.percentile)}
              size="small"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Entity Benchmarks */}
      {Object.keys(benchmarks.entities).length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Entity Activity
            </Typography>

            {Object.entries(benchmarks.entities).map(([entityType, entityData], index) => (
              <Box key={entityType}>
                {index > 0 && <Divider sx={{ my: 2 }} />}

                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {formatEntityName(entityType)}
                </Typography>

                <Box display="flex" gap={2} mt={0.5}>
                  <Box flex="1">
                    <Typography variant="caption" color="text.secondary">
                      Your Count
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {entityData.accountCount}
                    </Typography>
                  </Box>
                  <Box flex="1">
                    <Typography variant="caption" color="text.secondary">
                      Average
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {entityData.avgCount}
                    </Typography>
                  </Box>
                  <Box flex="1">
                    <Typography variant="caption" color="text.secondary">
                      Top 25%
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {entityData.top25Count}
                    </Typography>
                  </Box>
                </Box>

                <Box mt={1}>
                  <Chip
                    label={`${entityData.percentile}th percentile`}
                    color={getPercentileColor(entityData.percentile)}
                    size="small"
                  />
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Time to First Entity */}
      {benchmarks.timeToFirstEntity && (
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Time to First Entity
            </Typography>

            <Box display="flex" gap={2}>
              <Box flex="1">
                <Typography variant="body2" color="text.secondary">
                  Your Time
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {benchmarks.timeToFirstEntity.accountTTF} days
                </Typography>
              </Box>
              <Box flex="1">
                <Typography variant="body2" color="text.secondary">
                  Average
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {benchmarks.timeToFirstEntity.avgTTF} days
                </Typography>
              </Box>
              <Box flex="1">
                <Typography variant="body2" color="text.secondary">
                  Top 25% (fastest)
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {benchmarks.timeToFirstEntity.top25TTF} days
                </Typography>
              </Box>
            </Box>

            <Box mt={2}>
              <Tooltip title="Lower time is better - percentile reflects speed of adoption">
                <Chip
                  label={`${benchmarks.timeToFirstEntity.percentile}th percentile (Faster than ${benchmarks.timeToFirstEntity.percentile}%)`}
                  color={getPercentileColor(benchmarks.timeToFirstEntity.percentile)}
                  size="small"
                />
              </Tooltip>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Activity Benchmark */}
      {benchmarks.activity && (
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Recent Activity
            </Typography>

            <Box display="flex" gap={2}>
              <Box flex="1">
                <Typography variant="body2" color="text.secondary">
                  Days Since Last Activity
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {benchmarks.activity.accountDays}
                </Typography>
              </Box>
              <Box flex="1">
                <Typography variant="body2" color="text.secondary">
                  Average
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {benchmarks.activity.avgDays}
                </Typography>
              </Box>
              <Box flex="1">
                <Typography variant="body2" color="text.secondary">
                  Top 25% (most active)
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {benchmarks.activity.top25Days}
                </Typography>
              </Box>
            </Box>

            <Box mt={2}>
              <Tooltip title="Lower days is better - percentile reflects recency of activity">
                <Chip
                  label={`${benchmarks.activity.percentile}th percentile`}
                  color={getPercentileColor(benchmarks.activity.percentile)}
                  size="small"
                />
              </Tooltip>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Cohort Comparison */}
      {benchmarks.cohort && (
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Similar Age Cohort ({benchmarks.cohort.cohortAgeRange})
            </Typography>

            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Comparing to {benchmarks.cohort.cohortSize} accounts with similar age
            </Typography>

            <Box display="flex" gap={2}>
              <Box flex="1">
                <Typography variant="body2" color="text.secondary">
                  Your Health
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {benchmarks.cohort.accountHealthScore}
                </Typography>
              </Box>
              <Box flex="1">
                <Typography variant="body2" color="text.secondary">
                  Cohort Average
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {benchmarks.cohort.avgHealthScore}
                </Typography>
              </Box>
              <Box flex="1">
                <Typography variant="body2" color="text.secondary">
                  Top 25% in Cohort
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {benchmarks.cohort.top25HealthScore}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Stage Comparison */}
      {benchmarks.stage && (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Stage: {benchmarks.stage.stage.replace(/_/g, ' ')}
            </Typography>

            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Comparing to {benchmarks.stage.accountsInStage} accounts in the same stage
            </Typography>

            <Box display="flex" gap={2}>
              <Box flex="1">
                <Typography variant="body2" color="text.secondary">
                  Your Health
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {benchmarks.stage.accountHealthScore}
                </Typography>
              </Box>
              <Box flex="1">
                <Typography variant="body2" color="text.secondary">
                  Stage Average
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {benchmarks.stage.avgHealthScore}
                </Typography>
              </Box>
              <Box flex="1">
                <Typography variant="body2" color="text.secondary">
                  Top 25% in Stage
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {benchmarks.stage.top25HealthScore}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
